import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { ensureReadableColor } from '../utils/colorUtils';

// Crown icon for primary tag
const CrownIcon = () => (
  <svg 
    className="task-node__tag-crown" 
    viewBox="0 0 24 24" 
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M5 16L3 5l5.5 5L12 4l3.5 6L21 5l-2 11H5zm14 3c0 .6-.4 1-1 1H6c-.6 0-1-.4-1-1v-1h14v1z"/>
  </svg>
);

/**
 * TriggerButton Component
 * 
 * Shows as many tag pills as fit (at their natural width), then circles for overflow.
 * Pills never shrink - they're fixed width based on their text.
 * Primary tag always shows first with a crown icon.
 * Dynamically recalculates on resize.
 */
const TriggerButton = React.forwardRef(({
  currentTags,
  primaryTag,
  primaryTagObject,
  allTagObjects,
  onClick,
}, ref) => {
  const hasValue = currentTags.length > 0;
  const rowRef = useRef(null); // Container row - stable width reference (flex: 1)
  const btnRef = useRef(null);
  const measureRef = useRef(null);
  const [visibleCount, setVisibleCount] = useState(allTagObjects?.length || 0);
  
  // Sort tags so primary is always first
  const sortedTagObjects = useMemo(() => {
    if (!allTagObjects?.length) return [];
    if (!primaryTag) return allTagObjects;
    
    const sorted = [...allTagObjects];
    const primaryIndex = sorted.findIndex(t => t.name === primaryTag);
    if (primaryIndex > 0) {
      const [primary] = sorted.splice(primaryIndex, 1);
      sorted.unshift(primary);
    }
    return sorted;
  }, [allTagObjects, primaryTag]);
  
  // Calculate how many pills fit at their natural width
  const calculateFit = useCallback(() => {
    if (!measureRef.current || !rowRef.current || !sortedTagObjects?.length) return;
    
    const pills = measureRef.current.querySelectorAll('.task-node__tag-pill--measure');
    if (pills.length === 0) return;
    
    // Available width is the ROW's width (not the button's)
    // The row has flex: 1 so its width is stable and determined by parent layout
    // The button has width: fit-content, so measuring it would create a feedback loop
    const availableWidth = rowRef.current.offsetWidth;
    
    // Space needed for overflow indicator (circles + "+X")
    const overflowWidth = 60;
    
    // Measure each pill's natural width
    const pillWidths = Array.from(pills).map(pill => pill.offsetWidth);
    const gap = 4;
    
    let totalWidth = 0;
    let fitCount = 0;
    
    for (let i = 0; i < pillWidths.length; i++) {
      const pillWidth = pillWidths[i];
      const widthWithGap = pillWidth + (fitCount > 0 ? gap : 0);
      
      // Check if this pill fits
      // If there are more pills after this one, reserve space for overflow indicator
      const hasMorePills = i < pillWidths.length - 1;
      const spaceNeeded = totalWidth + widthWithGap + (hasMorePills ? overflowWidth : 0);
      
      if (spaceNeeded <= availableWidth) {
        totalWidth += widthWithGap;
        fitCount++;
      } else {
        break;
      }
    }
    
    // Always show at least 1 pill if there are tags
    setVisibleCount(Math.max(1, fitCount));
  }, [sortedTagObjects]);
  
  // Recalculate on mount and when tags change
  useEffect(() => {
    // Small delay to ensure DOM is ready
    const timer = setTimeout(calculateFit, 0);
    return () => clearTimeout(timer);
  }, [calculateFit, sortedTagObjects]);
  
  // Watch for resize using ResizeObserver on the ROW (not the button)
  // The row has stable width from parent layout, avoiding feedback loops
  // IMPORTANT: Debounce the resize callback to prevent layout thrashing during
  // transient render states (e.g., when selection changes cause re-renders)
  useEffect(() => {
    if (!rowRef.current) return;
    
    let resizeTimer = null;
    const resizeObserver = new ResizeObserver(() => {
      // Debounce to avoid triggering on transient layout states
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeTimer = setTimeout(calculateFit, 50);
    });
    
    resizeObserver.observe(rowRef.current);
    
    return () => {
      if (resizeTimer) clearTimeout(resizeTimer);
      resizeObserver.disconnect();
    };
  }, [calculateFit]);
  
  // Tags to show as pills vs circles (using sorted order)
  const visibleTags = sortedTagObjects.slice(0, visibleCount);
  const overflowTags = sortedTagObjects.slice(visibleCount);
  // Show up to 3 circles for overflow
  const circleTags = overflowTags.slice(0, 3);
  const overflowCount = overflowTags.length;

  return (
    <div className="task-node__tags-row" ref={rowRef}>
      {/* Hidden measurement container - always renders all pills at natural width */}
      {hasValue && (
        <span className="task-node__tags-measure" ref={measureRef} aria-hidden="true">
          {sortedTagObjects.map((tag) => {
            const isPrimary = tag.name === primaryTag;
            return (
              <span 
                key={tag.name}
                className="task-node__tag-pill task-node__tag-pill--measure"
                style={{ '--pill-color': ensureReadableColor(tag.color) }}
              >
                {isPrimary && <CrownIcon />}
                {tag.name}
              </span>
            );
          })}
        </span>
      )}
      
      <button 
        ref={(el) => {
          btnRef.current = el;
          if (typeof ref === 'function') ref(el);
          else if (ref) ref.current = el;
        }}
        className={`task-node__tags-btn ${hasValue ? 'task-node__tags-btn--has-value' : ''}`}
        onClick={onClick}
        onMouseDown={(e) => e.stopPropagation()}
        title={hasValue ? `${currentTags.join(', ')} - Click to add more tags` : 'Add tags'}
      >
        {hasValue ? (
          <span className="task-node__tags-content">
            {/* Visible tags as pills - at natural width */}
            {visibleTags.map((tag) => {
              const isPrimary = tag.name === primaryTag;
              return (
                <span 
                  key={tag.name}
                  className={`task-node__tag-pill ${isPrimary ? 'task-node__tag-pill--primary' : ''}`}
                  style={{ '--pill-color': ensureReadableColor(tag.color) }}
                >
                  {isPrimary && <CrownIcon />}
                  {tag.name}
                </span>
              );
            })}
            
            {/* Overflow tags as circles + count */}
            {overflowCount > 0 && (
              <span className="task-node__tag-circles">
                {circleTags.map((tag) => (
                  <span
                    key={tag.name}
                    className="task-node__tag-circle"
                    style={{ '--circle-color': ensureReadableColor(tag.color) }}
                    title={tag.name}
                  />
                ))}
                <span className="task-node__tag-more">+{overflowCount}</span>
              </span>
            )}
            
            {/* Add more tags indicator */}
            <span className="task-node__tags-add-more" title="Add more tags">
              +
            </span>
          </span>
        ) : (
          <span className="task-node__tags-placeholder">+ Tag</span>
        )}
      </button>
    </div>
  );
});

TriggerButton.displayName = 'TriggerButton';

export default TriggerButton;

